// Lead Nurture → Analytics (managers and admins). Data: GET /api/nurture/analytics (routes/nurture.py).
import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Legend, LabelList, Cell,
} from "recharts";
import { getNurtureAnalytics } from "@/lib/api";

// Chart colours: the dark-mode categorical slots 1–3 (blue, orange, aqua), validated on the #0f0f0f surface.
const C = { blue: "#3987e5", orange: "#d95926", aqua: "#199e70", muted: "#52525b", grid: "#1f1f1f" };
const TIP = { background: "#0f0f0f", border: "1px solid #262626", borderRadius: 8, fontSize: 12 };
const COUNTRIES = ["India", "Sri Lanka", "Dubai", "All"];
const IST = "Asia/Kolkata";

const ymd = (d) => d.toLocaleDateString("en-CA", { timeZone: IST });          // YYYY-MM-DD in India time
function presetRange(key) {
  const now = new Date();
  const today = ymd(now);
  const back = (n) => ymd(new Date(now.getTime() - n * 86400000));
  if (key === "today") return [today, today];
  if (key === "30") return [back(29), today];
  if (key === "month") return [`${today.slice(0, 8)}01`, today];
  return [back(6), today];
}
const PRESETS = [["today", "Today"], ["7", "Last 7 days"], ["30", "Last 30 days"], ["month", "This month"], ["custom", "Custom"]];

const pct = (v) => (v == null ? "—" : `${Math.round(v)}%`);
const num = (v) => (v == null ? "—" : v);
const mins = (v) => (v == null ? "—" : v < 60 ? `${Math.round(v)} min` : `${(v / 60).toFixed(1)} h`);
const hm = (s) => {
  if (!s) return "0:00";
  const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60);
  return h ? `${h} h ${String(m).padStart(2, "0")} m` : `${m}:${String(s % 60).padStart(2, "0")}`;
};
const shortGym = (name) => (name || "").split(" - ").pop();
const dayLabel = (d) => new Date(`${d}T12:00:00`).toLocaleDateString([], { day: "numeric", month: "short" });

function Tile({ label, value, sub, tone }) {
  return (
    <div className="card-surface px-4 py-3 min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 truncate" title={label}>{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 tabular-nums ${tone || "text-white"}`}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5 truncate" title={sub}>{sub}</div>}
    </div>
  );
}

function Panel({ title, note, children }) {
  return (
    <section className="card-surface p-4 min-w-0">
      <h3 className="text-sm font-semibold">{title}</h3>
      {note && <p className="text-[11px] text-zinc-500 mt-0.5">{note}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

const Empty = ({ children }) => <div className="h-[220px] grid place-items-center text-sm text-zinc-500 text-center px-4">{children}</div>;

export default function NurtureAnalytics({ initialCountry, onPickRep }) {
  const [preset, setPreset] = useState("7");
  const [range, setRange] = useState(() => presetRange("7"));
  const [country, setCountry] = useState(initialCountry && COUNTRIES.includes(initialCountry) ? initialCountry : "All");
  const [gym, setGym] = useState("");
  const [shift, setShift] = useState("all");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!range[0] || !range[1] || range[0] > range[1]) return undefined;
    let live = true;
    setLoading(true);
    getNurtureAnalytics({ from: range[0], to: range[1], country, facility_id: gym || undefined, shift })
      .then((d) => { if (live) { setData(d); setError(null); } })
      .catch((e) => { if (live) setError(e?.response?.data?.detail || "Couldn't load the analytics"); })
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [range, country, gym, shift]);

  const pick = (k) => { setPreset(k); if (k !== "custom") setRange(presetRange(k)); };
  const t = data?.tiles;
  const gymsInCountry = data?.facilities || [];

  return (
    <div className="space-y-4" data-testid="nurture-analytics">
      {/* Filters: one row above everything */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {PRESETS.map(([k, label]) => (
          <button key={k} onClick={() => pick(k)} data-testid={`period-${k}`}
            className={`rounded-full px-3 py-1 border ${preset === k ? "border-rose-500 bg-rose-500/10 text-white" : "border-[#262626] text-zinc-400"}`}>
            {label}
          </button>
        ))}
        {preset === "custom" && (
          <span className="flex items-center gap-1.5">
            <input type="date" value={range[0]} max={range[1]} onChange={(e) => setRange([e.target.value, range[1]])} aria-label="From"
              className="h-8 rounded-md border border-[#262626] bg-[#0b0b0b] px-2 text-sm text-zinc-200" />
            <span className="text-zinc-500">to</span>
            <input type="date" value={range[1]} min={range[0]} onChange={(e) => setRange([range[0], e.target.value])} aria-label="To"
              className="h-8 rounded-md border border-[#262626] bg-[#0b0b0b] px-2 text-sm text-zinc-200" />
          </span>
        )}
        <span className="w-px h-5 bg-[#262626] mx-1 hidden sm:block" />
        <select value={country} onChange={(e) => { setCountry(e.target.value); setGym(""); }} aria-label="Country"
          className="h-8 rounded-md border border-[#262626] bg-[#0b0b0b] px-2 text-sm text-zinc-200" data-testid="analytics-country">
          {COUNTRIES.map((c) => <option key={c} value={c}>{c === "All" ? "All countries" : c}</option>)}
        </select>
        <select value={gym} onChange={(e) => setGym(e.target.value)} aria-label="Gym"
          className="h-8 rounded-md border border-[#262626] bg-[#0b0b0b] px-2 text-sm text-zinc-200 max-w-[200px]" data-testid="analytics-gym">
          <option value="">All gyms</option>
          {gymsInCountry.map((g) => <option key={g.id} value={g.id}>{shortGym(g.name)}</option>)}
        </select>
        <select value={shift} onChange={(e) => setShift(e.target.value)} aria-label="Shift time"
          className="h-8 rounded-md border border-[#262626] bg-[#0b0b0b] px-2 text-sm text-zinc-200" data-testid="analytics-shift">
          <option value="all">Any time</option>
          <option value="on">During the rep&apos;s shift</option>
          <option value="off">Outside the rep&apos;s shift</option>
        </select>
        {loading && <span className="text-xs text-zinc-500">Loading…</span>}
        {data && <span className="text-xs text-zinc-500 ml-auto">{dayLabel(data.from)} – {dayLabel(data.to)} · {data.tz === IST ? "India time" : data.tz}</span>}
      </div>

      {error && <div className="rounded-lg border border-rose-500/30 bg-rose-500/[0.07] px-4 py-2.5 text-sm text-rose-200">{error}</div>}
      {!data && !error && <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="analytics-tiles">
            <Tile label="New leads" value={t.leads} sub={t.waiting_first_call ? `${t.waiting_first_call} still inside their first 10 min` : "landed in the period"} />
            <Tile label="Called within 10 min" value={pct(t.called_10min_pct)} sub={t.speed_judged ? `${t.called_10min} of ${t.speed_judged}` : "no leads yet"} />
            <Tile label="Median time to first call" value={mins(t.median_minutes_to_first_call)} sub={`${t.called} lead${t.called === 1 ? "" : "s"} called`} />
            <Tile label="Contact rate" value={pct(t.contact_rate)} sub={`${t.connected} of ${t.called} called reached`} />
            <Tile label="Consults booked" value={t.booked} sub={`booking rate ${pct(t.booking_rate)}`} />
            <Tile label="Show rate" value={pct(t.show_rate)} sub={`${t.showed} of ${t.past} past consults${t.unresolved ? ` · ${t.unresolved} no result yet` : ""}`} />
            <Tile label="Sold" value={t.sold} sub="from the FPA consult (phone match)" tone="text-emerald-300" />
            <Tile label="Close rate" value={pct(t.close_rate)} sub={`${t.sold} of ${t.showed} who showed`} />
            <Tile label="No-shows rebooked" value={pct(t.rebook_rate)} sub={`${t.rebooked} of ${t.noshow} no-shows`} />
            <Tile label="Overdue right now" value={t.overdue_now} tone={t.overdue_now ? "text-rose-300" : "text-white"}
              sub={t.overdue_never_called ? `${t.overdue_never_called} never called` : "redials + first calls"} />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Panel title="Funnel" note="Leads that landed in the period, followed to today. % = conversion from the step before.">
              {t.leads === 0 ? <Empty>No leads landed in this period.</Empty> : <Funnel rows={data.funnel} />}
            </Panel>
            <Panel title="Speed to lead" note="Lead landing (or 9 am, if it landed overnight) to the first call.">
              {t.leads === 0 ? <Empty>No leads landed in this period.</Empty> : <Speed rows={data.speed} waiting={data.speed_waiting} />}
            </Panel>
          </div>

          <Panel title="Team performance" note={`What each rep did in the period${shift === "on" ? ", counting only calls and bookings during their shifts" : shift === "off" ? ", counting only calls and bookings outside their shifts" : ""}. Calls per shift hour = calls made on shift ÷ hours on shift (Shifts tab, leave and covers included). Click a rep to see their calls.`}>
            <RepTable rows={data.reps} onPick={onPickRep} />
          </Panel>

          <div className="grid lg:grid-cols-[3fr_2fr] gap-4">
            <Panel title="Daily" note="Each on the day it happened: leads landed, consults booked, consults attended.">
              <Trend rows={data.trend} />
            </Panel>
            <Panel title="By gym" note="Leads that landed in the period, followed to today.">
              <GymTable gyms={data.gyms} countries={data.countries} showCountries={country === "All" && !gym} />
            </Panel>
          </div>

          {data.confirmations && (
            <Panel title="Confirmation calls" note="Consults whose appointment falls in the period. Done = the lead was reached on a confirmation call before the consult.">
              <Confirmations c={data.confirmations} />
            </Panel>
          )}

          {data.heads_up_replies && (
            <Panel title="WhatsApp replies to the heads-up" note="Heads-ups sent in the period: how leads replied before the call, and whether the callbacks they chose on WhatsApp were kept.">
              <Replies r={data.heads_up_replies} />
            </Panel>
          )}

          <details className="text-xs text-zinc-500 card-surface px-4 py-3">
            <summary className="cursor-pointer text-zinc-400">How these numbers are counted</summary>
            <ul className="mt-2 space-y-1.5 list-disc pl-4">
              <li><b className="text-zinc-300">Tiles, funnel, speed to lead and by gym</b> follow the leads that landed in the period (each lead once), up to today. A lead from Monday booked on Thursday counts as booked.</li>
              <li><b className="text-zinc-300">Team performance and Daily</b> count what happened in the period: calls made, consults booked, consults attended, whatever day the lead landed. A lead counts once per rep who booked it, so a no-show rebooked by someone else counts for both reps.</li>
              <li><b className="text-zinc-300">Called within 10 min</b>: the clock starts when the lead lands, or at 9 am gym time if it landed overnight. Leads still inside their first 10 minutes aren&apos;t counted yet; leads never called count as missed.</li>
              <li><b className="text-zinc-300">Connected</b>: an Exotel call with talk time, or the rep logged Consult booked, Call back later or Not interested. Direct and WhatsApp calls with no outcome logged can&apos;t be known and count as not connected; Wrong number doesn&apos;t count.</li>
              <li><b className="text-zinc-300">Showed</b>: GoHighLevel marked the appointment Showed, or an FPA New Consult exists for the lead&apos;s phone at the same gym. Show rate = showed ÷ booked leads whose appointment time has passed (or that already have a result). Past consults with no result in GoHighLevel yet count as not showed.</li>
              <li><b className="text-zinc-300">Sold</b>: that FPA consult is marked sold or sold pending payment, matched by the last 9 digits of the phone and the gym. Estimated: a lead who gave a different number at the consult won&apos;t match.</li>
              <li><b className="text-zinc-300">Avg attempts</b>: calls on the lead (anyone&apos;s) up to the booking. <b className="text-zinc-300">AI score, script and tone</b>: averages over recorded calls that have been rated (n shown). <b className="text-zinc-300">Talk time</b>: Exotel calls only.</li>
              <li><b className="text-zinc-300">Confirmation rate</b>: confirmation calls done before the consult ÷ confirmations that fell due (the window opened before the consult, and before it was moved). Consults booked within 3 hours of the window opening count as confirmed at booking and aren&apos;t due. <b className="text-zinc-300">Show rate confirmed vs unconfirmed</b>: over consults with a result in GoHighLevel; confirmed includes confirmed at booking.</li>
              <li><b className="text-zinc-300">WhatsApp replies</b>: replies to the call heads-up, read from GoHighLevel for 30 minutes after it. Not now includes replies that named a time. Chose a callback = the lead picked a choice or a time; kept = someone called the lead between 15 minutes before and an hour after it.</li>
              <li><b className="text-zinc-300">Overdue</b> is right now, not for the period: next attempt over an hour late. Nobody owns a lead before booking, so overdue redials count against whoever called the lead last.</li>
            </ul>
          </details>
        </>
      )}
    </div>
  );
}

function Funnel({ rows }) {
  const data = rows.map((r, i) => ({ ...r, label: i === 0 ? `${r.value}` : `${r.value} · ${pct(r.step_pct)}` }));
  return (
    <div style={{ height: 240 }} data-testid="analytics-funnel">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 80, left: 0, bottom: 0 }} barCategoryGap={6}>
          <CartesianGrid stroke={C.grid} horizontal={false} />
          <XAxis type="number" hide allowDecimals={false} />
          <YAxis type="category" dataKey="stage" stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} width={80} />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }} contentStyle={TIP}
            formatter={(v, _n, p) => [`${v} leads${p.payload.step_pct != null ? ` (${pct(p.payload.step_pct)} of the step before, ${pct(p.payload.of_leads_pct)} of all leads)` : ""}`, p.payload.stage]} />
          <Bar dataKey="value" fill={C.blue} radius={[0, 4, 4, 0]} isAnimationActive={false}>
            <LabelList dataKey="label" position="right" fill="#d4d4d8" fontSize={12} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Speed({ rows, waiting }) {
  return (
    <div data-testid="analytics-speed">
      <div style={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 16, right: 4, left: -24, bottom: 0 }}>
            <CartesianGrid stroke={C.grid} vertical={false} />
            <XAxis dataKey="bucket" stroke="#52525b" fontSize={11} tickLine={false} axisLine={{ stroke: C.grid }} interval={0} />
            <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }} contentStyle={TIP} formatter={(v) => [`${v} leads`, "First call"]} />
            <Bar dataKey="leads" radius={[4, 4, 0, 0]} isAnimationActive={false}>
              {rows.map((r, i) => <Cell key={r.bucket} fill={r.bucket === "Never called" ? C.muted : i < 2 ? C.aqua : C.blue} />)}
              <LabelList dataKey="leads" position="top" fill="#a1a1aa" fontSize={11} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px] text-zinc-500 mt-1">
        <span className="inline-block h-2 w-2 rounded-sm mr-1 align-middle" style={{ background: C.aqua }} />within the 10-minute standard
        <span className="inline-block h-2 w-2 rounded-sm ml-3 mr-1 align-middle" style={{ background: C.blue }} />later
        <span className="inline-block h-2 w-2 rounded-sm ml-3 mr-1 align-middle" style={{ background: C.muted }} />never called
        {waiting > 0 && <span className="ml-3">{waiting} not shown: still inside their first 10 minutes</span>}
      </p>
    </div>
  );
}

function Trend({ rows }) {
  const empty = rows.every((r) => !r.leads && !r.booked && !r.showed);
  if (empty) return <Empty>Nothing landed, was booked or attended in this period.</Empty>;
  const data = rows.map((r) => ({ ...r, label: dayLabel(r.day) }));
  const single = data.length === 1;
  return (
    <div style={{ height: 240 }} data-testid="analytics-trend">
      <ResponsiveContainer width="100%" height="100%">
        {single ? (
          <BarChart data={data} margin={{ top: 8, right: 8, left: -24, bottom: 0 }} barGap={2}>
            <CartesianGrid stroke={C.grid} vertical={false} />
            <XAxis dataKey="label" stroke="#52525b" fontSize={11} tickLine={false} axisLine={{ stroke: C.grid }} />
            <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }} contentStyle={TIP} />
            <Legend wrapperStyle={{ fontSize: 12, color: "#a1a1aa" }} iconType="circle" iconSize={8} />
            <Bar dataKey="leads" name="New leads" fill={C.blue} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            <Bar dataKey="booked" name="Booked" fill={C.orange} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            <Bar dataKey="showed" name="Showed" fill={C.aqua} radius={[4, 4, 0, 0]} isAnimationActive={false} />
          </BarChart>
        ) : (
          <LineChart data={data} margin={{ top: 8, right: 12, left: -24, bottom: 0 }}>
            <CartesianGrid stroke={C.grid} vertical={false} />
            <XAxis dataKey="label" stroke="#52525b" fontSize={11} tickLine={false} axisLine={{ stroke: C.grid }} minTickGap={16} />
            <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip contentStyle={TIP} cursor={{ stroke: "#3f3f46" }} />
            <Legend wrapperStyle={{ fontSize: 12, color: "#a1a1aa" }} iconType="circle" iconSize={8} />
            <Line type="monotone" dataKey="leads" name="New leads" stroke={C.blue} strokeWidth={2} dot={{ r: 3, fill: C.blue }} isAnimationActive={false} />
            <Line type="monotone" dataKey="booked" name="Booked" stroke={C.orange} strokeWidth={2} dot={{ r: 3, fill: C.orange }} isAnimationActive={false} />
            <Line type="monotone" dataKey="showed" name="Showed" stroke={C.aqua} strokeWidth={2} dot={{ r: 3, fill: C.aqua }} isAnimationActive={false} />
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

const REP_COLS = [
  ["name", "Rep"], ["calls", "Calls"], ["connected", "Connected"], ["talk_seconds", "Talk time"],
  ["avg_attempts_to_book", "Avg attempts to book"], ["booked", "Booked"], ["show_rate", "Show rate"], ["sold", "Sold"],
  ["avg_score", "AI score"], ["avg_adherence", "Script"], ["avg_tone", "Tone"],
  ["median_minutes_to_first_call", "Speed to first call"], ["overdue_redials", "Overdue now"],
  ["shift_hours", "Shift hours"], ["calls_per_shift_hour", "Calls / shift hour"],
];

function ScoreCell({ v, n }) {
  if (v == null) return <span className="text-zinc-600">—</span>;
  const tone = v >= 75 ? "text-emerald-400" : v >= 50 ? "text-amber-300" : "text-rose-400";
  return <span><span className={`font-semibold ${tone}`}>{Math.round(v)}</span>{n != null && <span className="text-[10px] text-zinc-500 ml-1">n={n}</span>}</span>;
}

function RepTable({ rows, onPick }) {
  const [sort, setSort] = useState(["booked", -1]);
  const sorted = useMemo(() => {
    const [k, dir] = sort;
    return [...rows].sort((a, b) => {
      const x = a[k]; const y = b[k];
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      return (typeof x === "string" ? x.localeCompare(y) : x - y) * dir;
    });
  }, [rows, sort]);
  if (!rows.length) return <div className="py-6 text-center text-sm text-zinc-500">No calls or bookings in this period.</div>;
  const click = (k) => setSort(([sk, d]) => [k, sk === k ? -d : k === "name" || k === "median_minutes_to_first_call" ? 1 : -1]);
  return (
    <div className="overflow-x-auto -mx-4 px-4">
      <table className="w-full text-sm" data-testid="analytics-reps">
        <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
          <tr className="text-left">
            {REP_COLS.map(([k, label]) => (
              <th key={k} className="px-2 py-2 font-medium whitespace-nowrap">
                <button onClick={() => click(k)} className="inline-flex items-center gap-1 hover:text-zinc-200" aria-label={`Sort by ${label}`}>
                  {label}
                  {sort[0] === k && (sort[1] < 0 ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[#1a1a1a] tabular-nums">
          {sorted.map((r) => (
            <tr key={r.user_id} className="hover:bg-[#111]">
              <td className="px-2 py-2 font-medium whitespace-nowrap">
                {onPick ? <button className="hover:underline text-left" onClick={() => onPick(r.user_id, r.name)} data-testid={`rep-${r.user_id}`}>{r.name}</button> : r.name}
              </td>
              <td className="px-2 py-2">{r.calls}</td>
              <td className="px-2 py-2">{r.connected}{r.calls ? <span className="text-[10px] text-zinc-500 ml-1">{pct((100 * r.connected) / r.calls)}</span> : null}</td>
              <td className="px-2 py-2 whitespace-nowrap">{hm(r.talk_seconds)}</td>
              <td className="px-2 py-2">{num(r.avg_attempts_to_book)}</td>
              <td className={`px-2 py-2 ${r.booked ? "text-white font-semibold" : "text-zinc-500"}`}>{r.booked}</td>
              <td className="px-2 py-2 whitespace-nowrap">{pct(r.show_rate)}{r.bookings_past ? <span className="text-[10px] text-zinc-500 ml-1">{r.showed}/{r.bookings_past}</span> : null}</td>
              <td className={`px-2 py-2 ${r.sold ? "text-emerald-300 font-semibold" : "text-zinc-500"}`}>{r.sold}</td>
              <td className="px-2 py-2 whitespace-nowrap"><ScoreCell v={r.avg_score} n={r.rated || null} /></td>
              <td className="px-2 py-2"><ScoreCell v={r.avg_adherence} /></td>
              <td className="px-2 py-2"><ScoreCell v={r.avg_tone} /></td>
              <td className="px-2 py-2 whitespace-nowrap">{mins(r.median_minutes_to_first_call)}{r.first_calls ? <span className="text-[10px] text-zinc-500 ml-1">n={r.first_calls}</span> : null}</td>
              <td className={`px-2 py-2 ${r.overdue_redials ? "text-rose-300 font-semibold" : "text-zinc-500"}`}>{r.overdue_redials}</td>
              <td className="px-2 py-2">{r.shift_hours ? `${r.shift_hours} h` : <span className="text-zinc-600">—</span>}</td>
              <td className="px-2 py-2">{num(r.calls_per_shift_hour)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GymRow({ r, label, strong }) {
  return (
    <tr className={strong ? "bg-[#111] font-semibold" : ""}>
      <td className="px-2 py-2 whitespace-nowrap">{label}</td>
      <td className="px-2 py-2">{r.leads}</td>
      <td className="px-2 py-2">{pct(r.called_10min_pct)}</td>
      <td className="px-2 py-2">{pct(r.called_1h_pct)}</td>
      <td className="px-2 py-2 whitespace-nowrap">{mins(r.median_minutes_to_first_call)}</td>
      <td className={`px-2 py-2 ${r.never_called ? "text-amber-300" : ""}`}>{r.never_called ?? "—"}{r.never_called ? <span className="text-[10px] text-zinc-500 ml-1">{pct(r.never_called_pct)}</span> : null}</td>
      <td className="px-2 py-2">{r.booked}</td>
      <td className="px-2 py-2">{r.showed}</td>
      <td className={`px-2 py-2 ${r.sold ? "text-emerald-300" : ""}`}>{r.sold}</td>
    </tr>
  );
}

function GymTable({ gyms, countries, showCountries }) {
  if (!gyms.length) return <div className="py-6 text-center text-sm text-zinc-500">No gyms here.</div>;
  const groups = showCountries ? countries.map((c) => ({ c, gyms: gyms.filter((g) => g.country === c.country) })) : [{ c: null, gyms }];
  return (
    <div className="overflow-x-auto -mx-4 px-4">
      <table className="w-full text-sm tabular-nums" data-testid="analytics-gyms">
        <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
          <tr className="text-left">
            {["Gym", "Leads", "Called ≤10 min", "≤1 h", "Median wait", "Never called", "Booked", "Showed", "Sold"].map((h) => <th key={h} className="px-2 py-2 font-medium whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-[#1a1a1a]">
          {groups.map(({ c, gyms: gs }) => (
            [
              ...gs.map((g) => <GymRow key={g.facility_id} r={g} label={shortGym(g.gym)} />),
              c && <GymRow key={`c-${c.country}`} r={c} label={`${c.country} total`} strong />,
            ]
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Confirmations({ c }) {
  const t = c.tiles;
  const ratio = (a, b) => (b ? `${a} of ${b}` : "none yet");
  return (
    <div className="space-y-3" data-testid="analytics-confirmations">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Tile label="Confirmation rate" value={pct(t.rate)} sub={`${ratio(t.done, t.due)} due done before the consult`} />
        <Tile label="Confirmed at booking" value={t.at_booking} sub="booked within 3 h of the window" />
        <Tile label="Show rate · confirmed" value={pct(t.show_rate_confirmed)} sub={ratio(t.confirmed_showed, t.confirmed_n)} tone="text-emerald-300" />
        <Tile label="Show rate · unconfirmed" value={pct(t.show_rate_unconfirmed)} sub={ratio(t.unconfirmed_showed, t.unconfirmed_n)}
          tone={t.unconfirmed_n ? "text-amber-300" : "text-white"} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm tabular-nums" data-testid="analytics-confirm-reps">
            <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
              <tr className="text-left">{["Rep", "Confirm calls", "Reached", "Show rate (confirmed by them)"].map((h) => <th key={h} className="px-2 py-2 font-medium whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-[#1a1a1a]">
              {c.reps.length === 0 && <tr><td colSpan={4} className="px-2 py-4 text-center text-zinc-500">No confirmation calls in this period.</td></tr>}
              {c.reps.map((r) => (
                <tr key={r.user_id}>
                  <td className="px-2 py-2 font-medium whitespace-nowrap">{r.name}</td>
                  <td className="px-2 py-2">{r.calls}</td>
                  <td className="px-2 py-2">{r.done}</td>
                  <td className="px-2 py-2">{pct(r.show_rate_confirmed)}{r.confirmed_n ? <span className="text-[10px] text-zinc-500 ml-1">{r.confirmed_showed}/{r.confirmed_n}</span> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm tabular-nums" data-testid="analytics-confirm-gyms">
            <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
              <tr className="text-left">{["Gym", "Due", "Done", "Rate", "Show · confirmed", "Show · unconfirmed"].map((h) => <th key={h} className="px-2 py-2 font-medium whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-[#1a1a1a]">
              {c.gyms.length === 0 && <tr><td colSpan={6} className="px-2 py-4 text-center text-zinc-500">No consults in this period.</td></tr>}
              {c.gyms.map((g) => (
                <tr key={g.facility_id}>
                  <td className="px-2 py-2 whitespace-nowrap">{shortGym(g.gym)}</td>
                  <td className="px-2 py-2">{g.due}</td>
                  <td className="px-2 py-2">{g.done}</td>
                  <td className="px-2 py-2">{pct(g.rate)}</td>
                  <td className="px-2 py-2">{pct(g.show_rate_confirmed)}</td>
                  <td className="px-2 py-2">{pct(g.show_rate_unconfirmed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Replies({ r }) {
  const ratio = (a, b) => (b ? `${a} of ${b}` : "none yet");
  return (
    <div className="space-y-2" data-testid="analytics-replies">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Tile label="Replied" value={pct(r.reply_rate)} sub={`${ratio(r.replied, r.watched)} heads-ups watched`} />
        <Tile label="Call me now" value={r.ok} sub="happy to take the call" tone="text-emerald-300" />
        <Tile label="Not now" value={r.not_now_total} sub={`${r.cancelled} call${r.cancelled === 1 ? "" : "s"} cancelled`} tone={r.not_now_total ? "text-amber-300" : "text-white"} />
        <Tile label="Stop" value={r.stop} sub="don't call / not interested" tone={r.stop ? "text-rose-300" : "text-white"} />
        <Tile label="Chose a callback" value={r.chosen} sub={`${r.followups} follow-up${r.followups === 1 ? "" : "s"} sent`} />
        <Tile label="Callback kept" value={pct(r.kept_rate)} sub={ratio(r.kept, r.kept_due)} />
      </div>
      {r.no_scope > 0 && (
        <p className="text-[11px] text-amber-300/90">
          {r.no_scope} heads-up{r.no_scope === 1 ? "" : "s"} couldn&apos;t be watched: the gym&apos;s GoHighLevel token needs the conversations.readonly and conversations/message.readonly scopes.
        </p>
      )}
    </div>
  );
}
